import { CalendarFacets, Maybe, PackagePriceBasis, Receipt } from '@gql/schema';
import { TravellersSelection, gatherChildrenAndAdultsFromRooms } from '@secretescapes/cauldron';
import { ITranslationsObject, i18n } from '@secretescapes/i18n';
import { groupBy } from '@utils/lodash.utils';
import { ExtendedOffer, ExtendedOfferFacet } from './Offers.interfaces';

export const ROOM_URGENCY_LIMIT = 5;

export const unavailableForDates = (showFinalRates: boolean, receipt: Maybe<Receipt>) => {
  return showFinalRates && !receipt;
};

export const unsuitableForTravellers = (offer: ExtendedOffer) => {
  return !offer.pricing;
};

export const notEnoughRooms = (numberOfRooms: number, offer: ExtendedOffer) => {
  return !!offer.pricing && numberOfRooms > (offer.pricing.maxAvailability ?? 0);
};

export const isBookable = (showFinalRates: boolean, receipt: Maybe<Receipt>) => {
  return showFinalRates && !!receipt;
};

export const lowRooms = (availableRoomCount: Maybe<number>) => {
  return availableRoomCount && availableRoomCount < ROOM_URGENCY_LIMIT;
};

export enum InfoBoxType {
  UNAVAILABLE_FOR_DATES = 'UNAVAILABLE_FOR_DATES',
  NOT_ENOUGH_ROOMS = 'NOT_ENOUGH_ROOMS',
  LOW_ROOMS = 'LOW_ROOMS',
  UNSUITABLE_ROOM_FOR_TRAVELLERS = 'UNSUITABLE_ROOM_FOR_TRAVELLERS',
}

export function getInfoBoxType(
  showFinalRates: boolean,
  offer: ExtendedOffer,
  travellersSelection: TravellersSelection
): InfoBoxType | null {
  if (notEnoughRooms(travellersSelection.rooms.length, offer)) {
    return InfoBoxType.NOT_ENOUGH_ROOMS;
  }

  if (unsuitableForTravellers(offer)) {
    return InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS;
  }

  if (unavailableForDates(showFinalRates, offer.receipt)) {
    return InfoBoxType.UNAVAILABLE_FOR_DATES;
  }

  if (isBookable(showFinalRates, offer.receipt) && lowRooms(offer.receipt?.maxAvailability)) {
    return InfoBoxType.LOW_ROOMS;
  }

  return null;
}

export function disableOffer(infoBoxType: InfoBoxType | null) {
  return (
    !!infoBoxType &&
    [
      InfoBoxType.UNAVAILABLE_FOR_DATES,
      InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS,
      InfoBoxType.NOT_ENOUGH_ROOMS,
    ].includes(infoBoxType)
  );
}

export const sortOffers = (
  offers: ExtendedOffer[],
  getInfoBox: (o: ExtendedOffer) => InfoBoxType | null
) => {
  // This sorts offers in the following order:
  // Available Rooms -> Unsuitable room for travellers -> Unavailable on dates selected -> Not enough rooms (rooms selected > maxAvailability)

  const groupedOffers = groupBy(offers, o => {
    const offerInfoBoxType = getInfoBox(o);

    switch (offerInfoBoxType) {
      case InfoBoxType.UNSUITABLE_ROOM_FOR_TRAVELLERS:
        return 1;
      case InfoBoxType.UNAVAILABLE_FOR_DATES:
        return 2;
      case InfoBoxType.NOT_ENOUGH_ROOMS:
        return 3;
      default:
        return 0;
    }
  });

  return Object.values(groupedOffers).flat();
};

export const getTravellersFromFacets = (packagePriceBasis: Maybe<PackagePriceBasis>) => {
  if (!packagePriceBasis) {
    return 0;
  }

  return (packagePriceBasis.adults ?? 0) + (packagePriceBasis.children ?? 0);
};

export const getFacetOffers = (facets: Maybe<CalendarFacets>): ExtendedOfferFacet[] => {
  return (facets?.offers ?? [])
    .filter(offer => offer.leadRate?.amount)
    .map(offer => ({
      ...offer,
      packagePriceBasis: facets?.packagePriceBasis,
    }));
};

// If flights offers page then remove 'Travel is not included' from offer descriptions
export const getFlightsDescription = (description: string, translations: ITranslationsObject) => {
  const travelNotIncludedText = i18n('offer.info.travel.not.included', [], translations);

  return description
    .split('</li>')
    .filter((el: string) => !el.match(travelNotIncludedText))
    .join('</li>');
};

export const showDisclaimer = (
  travellersSelection: TravellersSelection,
  packagePriceBasis: Maybe<PackagePriceBasis>
) => {
  const priceBasisTravellers = getTravellersFromFacets(packagePriceBasis);
  const { adults, children } = gatherChildrenAndAdultsFromRooms(travellersSelection.rooms);
  const totalTravellers = adults + children;

  return priceBasisTravellers !== totalTravellers;
};
